import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Play, Trash2, Repeat } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories, useDirectory, catName } from "@/lib/hooks";
import { PageHeader, Empty } from "@/components/common";
import { PropertySelect } from "@/components/FormBits";
import { Field } from "@/components/PropertyDialog";
import { DateInput } from "@/components/DateInput";

const blank = () => ({ property_id: "", title: "", description: "", category: "maintenance", frequency_days: 90, next_date: new Date().toISOString().slice(0, 10), provider_id: "" });

function PlanDialog({ open, setOpen, initial }) {
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const providers = useDirectory("prestataire");
  const qc = useQueryClient();
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try { await api.post("/maintenance/plans", { ...f, frequency_days: Number(f.frequency_days), provider_id: f.provider_id || null }); qc.invalidateQueries({ queryKey: ["plans"] }); qc.invalidateQueries({ queryKey: ["analytics"] }); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg rounded-sm" data-testid="plan-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newPlan")}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Field label={t("property")}><PropertySelect value={f.property_id} onChange={set("property_id")} testId="plan-property-select" /></Field></div>
          <div className="col-span-2"><Field label={t("title")}><Input value={f.title} onChange={set("title")} data-testid="plan-title-input" /></Field></div>
          <Field label={t("category")}>
            <Select value={f.category} onValueChange={set("category")}><SelectTrigger data-testid="plan-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.trade.map((c) => <SelectItem key={c.key} value={c.key}>{catName(c, lang)}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={t("frequency")}><Input type="number" value={f.frequency_days} onChange={set("frequency_days")} data-testid="plan-frequency-input" /></Field>
          <Field label={t("nextDate")}><DateInput value={f.next_date} onChange={set("next_date")} data-testid="plan-next-input" /></Field>
          <Field label={t("assignedTo")}>
            <Select value={f.provider_id} onValueChange={set("provider_id")}><SelectTrigger data-testid="plan-provider-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          </Field>
          <div className="col-span-2"><Field label={t("description")}><Textarea value={f.description} onChange={set("description")} data-testid="plan-description-input" /></Field></div>
        </div>
        <Button onClick={save} disabled={!f.property_id || !f.title} className="rounded-sm bg-[#0F172A]" data-testid="plan-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}

function SlaSettings() {
  const { t } = usePrefs();
  const { data } = useQuery({ queryKey: ["sla"], queryFn: () => fetcher("/settings/sla") });
  const [f, setF] = useState(null);
  const v = f || data;
  if (!v) return null;
  const save = async () => { try { await api.put("/settings/sla", Object.fromEntries(Object.entries(v).map(([k, x]) => [k, Number(x)]))); toast.success(t("save")); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="sla-settings">
      <h3 className="mb-3 font-display text-lg font-bold">{t("slaSettings")}</h3>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {["urgente", "haute", "normale", "basse"].map((k) => <Field key={k} label={t(k)}><Input type="number" value={v[k]} onChange={(e) => setF({ ...v, [k]: e.target.value })} data-testid={`sla-${k}-input`} /></Field>)}
      </div>
      <Button size="sm" onClick={save} className="mt-3 rounded-sm bg-[#0F172A]" data-testid="sla-save-button">{t("save")}</Button>
    </div>
  );
}

export default function Maintenance() {
  const { user } = useAuth();
  const { t, fdate, lang } = usePrefs();
  const { label } = useCategories();
  const qc = useQueryClient();
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  const { data: plans = [] } = useQuery({ queryKey: ["plans"], queryFn: () => fetcher("/maintenance/plans") });
  const { data: an } = useQuery({ queryKey: ["analytics"], queryFn: () => fetcher("/analytics") });
  const [dlg, setDlg] = useState(null);
  const run = async (fn, msg) => { try { await fn(); qc.invalidateQueries({ queryKey: ["plans"] }); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="maintenance-page">
      <PageHeader eyebrow={t("secOps")} title={t("maintenance")}>
        {staff && <Button onClick={() => setDlg(blank())} className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-plan-button"><Plus size={16} className="mr-1" />{t("newPlan")}</Button>}
      </PageHeader>
      {dlg && <PlanDialog open={!!dlg} setOpen={(o) => !o && setDlg(null)} initial={dlg} />}
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <section>
          {plans.length === 0 && <Empty />}
          <div className="divide-y divide-slate-100 rounded-sm border bg-white">
            {plans.map((p) => (
              <div key={p.id} className="grid gap-3 px-5 py-4 md:grid-cols-[1fr_auto] md:items-center" data-testid={`plan-row-${p.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.property_name} · {label(p.category)} · <Repeat size={11} className="inline" /> {p.frequency_days} {t("frequency").replace(/.*\((.*)\).*/, "$1")}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs">{t("nextDate")} : <b>{fdate(p.next_date)}</b></span>
                  {p.last_ticket_id && <Link to={`/tickets/${p.last_ticket_id}`} className="text-xs text-[#996515] underline">ticket</Link>}
                  {staff && <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={() => run(() => api.post(`/maintenance/plans/${p.id}/run`), "Ticket créé")} data-testid={`plan-run-${p.id}`}><Play size={12} className="mr-1" />{t("runNow")}</Button>}
                  {staff && <button onClick={() => run(() => api.delete(`/maintenance/plans/${p.id}`))} className="text-slate-500 hover:text-rose-600" data-testid={`plan-delete-${p.id}`}><Trash2 size={14} /></button>}
                </div>
              </div>
            ))}
          </div>
          {user.role === "admin" && <div className="mt-6"><SlaSettings /></div>}
        </section>
        <section className="rounded-sm border border-slate-200 bg-white p-5" data-testid="recurring-causes">
          <h3 className="mb-1 font-display text-lg font-bold">{t("recurring")}</h3>
          <p className="mb-4 text-xs text-slate-500">12 derniers mois · ≥ 2 {t("occurrences")}</p>
          {(an?.recurring || []).length === 0 && <p className="text-sm text-slate-500">{t("noData")}</p>}
          {(an?.recurring || []).map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-2 border-b border-slate-100 py-2.5 text-sm" data-testid={`recurring-row-${i}`}>
              <div><div className="font-semibold">{label(r.category)} <span className="font-data text-xs text-rose-700">×{r.count}</span></div><div className="text-xs text-slate-500">{r.property_name}{r.unit_ref ? ` · ${r.unit_ref}` : ""}</div></div>
              {staff && !r.has_plan && <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" data-testid={`recurring-plan-${i}`} onClick={() => setDlg({ ...blank(), property_id: r.property_id, category: r.category, unit_id: r.unit_id, title: `Contrôle préventif ${label(r.category)}${r.unit_ref ? ` — ${r.unit_ref}` : ""}` })}>{t("createPlan")}</Button>}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
